import {makeAutoObservable} from 'mobx';
import {ETableSort} from 'webapp-lib/Components';
import {Order} from 'webapp-lib/typings';

import {FolderType} from '#/FolderTree/Folder';
import {
  DeletedDocumentInResponse,
  DocumentInResponse,
} from '@lexian/api/documents-api';
import notificationsAsyncBackend, {
  UserAddedNewDocumentVersionPushNotification,
} from '@lexian/api/notifications-async-backend';
import {isAllItemsFolder, isTrashFolder} from '_/utilities/folders';
import {LoggedInUserInformation} from '_/utilities/types';

import {DocumentActionStore} from './DocumentActionStore';
import {DocumentFilters} from './DocumentFilters.store';

export enum SortField {
  Title = 'TITLE',
  LastModifiedAt = 'LAST_MODIFIED_AT',
  CreatedAt = 'CREATED_AT',
  FileSize = 'FILE_SIZE',
}

export const orderMap: Record<Order, 'ASC' | 'DESC'> = {
  [Order.Ascending]: 'ASC',
  [Order.Descending]: 'DESC',
};

export default class Store {
  public readonly casusId: string;

  public readonly filters: DocumentFilters = new DocumentFilters();

  public activeFolderId: string = FolderType.AllItems;
  public rootFolderId?: string;

  public activeDocumentVersionId?: string;

  public actionStore: DocumentActionStore = new DocumentActionStore();

  public sort: ETableSort<SortField>;

  public sharingHistoryDocument?:
    | DocumentInResponse
    | DeletedDocumentInResponse;

  public creatingDocumentFromTemplate = false;

  private readonly loggedInUser: LoggedInUserInformation;
  private invalidateDocumentList?: () => void;

  private unsubscribeDocumentsChangedTopic?: () => void;

  public constructor({
    casusId,
    loggedInUser,
  }: {
    casusId: string;
    loggedInUser: LoggedInUserInformation;
  }) {
    makeAutoObservable(this, {}, {autoBind: true});

    this.loggedInUser = loggedInUser;
    this.casusId = casusId;

    this.sort = [
      {columnName: SortField.LastModifiedAt, order: Order.Descending},
    ];

    notificationsAsyncBackend.initializeOnConnect(this.initializeSocket());
  }

  public unsubscribeAndRemoveEventListener(): void {
    this.unsubscribeDocumentsChangedTopic?.();
  }

  public setSort(sort: ETableSort<SortField>): void {
    this.sort = sort;
  }

  public setInvalidateDocumentList(invalidateData: () => void): void {
    this.invalidateDocumentList = invalidateData;
  }

  public initializeSocket(): () => void {
    return (): void => {
      this.unsubscribeDocumentsChangedTopic =
        notificationsAsyncBackend.topicDocumentManagementUserSpecific(
          {userId: this.loggedInUser.userId},
          (notification) => {
            if (
              notification.body.pushNotificationType ===
              'UserAddedNewDocumentVersionPushNotification'
            ) {
              const body =
                notification.body as UserAddedNewDocumentVersionPushNotification;
              if (
                body.casusId === this.casusId &&
                (body.folderId === this.activeFolderId ||
                  isAllItemsFolder(this.activeFolderId))
              ) {
                this.invalidateDocumentList?.();
              }
            }
          }
        ).unsubscribe;
    };
  }

  public get serverAcceptedActiveFolderId(): string {
    if (
      this.rootFolderId &&
      (isAllItemsFolder(this.activeFolderId) ||
        isTrashFolder(this.activeFolderId))
    ) {
      return this.rootFolderId;
    }
    return this.activeFolderId;
  }

  public setActiveFolderId(folderId: string): void {
    this.activeFolderId = folderId;
  }

  public setRootFolderId(folderId: string): void {
    this.rootFolderId = folderId;
  }

  public setActiveDocumentVersion(documentVersionId?: string): void {
    this.activeDocumentVersionId = documentVersionId;
  }

  public setSharingHistoryDocument([document]: (
    | DocumentInResponse
    | DeletedDocumentInResponse
  )[]): void {
    this.sharingHistoryDocument = document;
  }

  public clearSharingHistoryDocument(): void {
    this.sharingHistoryDocument = undefined;
  }

  public setCreatingDocumentFromTemplate(): void {
    this.creatingDocumentFromTemplate = true;
  }

  public clearCreatingDocumentFromTemplate(): void {
    this.creatingDocumentFromTemplate = false;
  }
}
